import React from 'react';

import { Divider, Skeleton, Typography } from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import RoundedBox from '@sharedCR/common/RoundedBox';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';

import TrackingValue from './TrackingValue';

const FinancialDetails = ({ isLoading, accountingConfig, shippingConfig, paymentConfig }) => {
  const renderGridSection = ({ title, config, withTracking }) => (
    <div className="flex flex-col gap-8">
      <Typography variant="medium16">{title}</Typography>
      <RoundedBox padding={16}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-16">
          {config.map(({ name, value, isModified, authors, trackingInfo }) => (
            <div className="flex flex-col gap-4" key={name}>
              {isLoading ? (
                <Skeleton height={50} variant="text" />
              ) : (
                <>
                  <Typography color="text.secondary" variant="regular14">
                    {name}
                  </Typography>
                  <PendingChangeWrapper
                    isSmall
                    authors={authors}
                    fieldLabel={name}
                    pendingChangesActionType={isModified ? PendingChangesActionType.MODIFIED : null}
                    textSelector="span"
                  >
                    {withTracking ? (
                      <TrackingValue trackingInfo={trackingInfo} value={value} />
                    ) : (
                      <Typography variant="bold16">{value}</Typography>
                    )}
                  </PendingChangeWrapper>
                </>
              )}
            </div>
          ))}
        </div>
      </RoundedBox>
    </div>
  );

  return (
    <div className="flex flex-col gap-16">
      {renderGridSection({ title: 'Accounting', config: accountingConfig })}
      {shippingConfig && (
        <>
          <Divider />
          {renderGridSection({
            title: 'Shipping & Tracking',
            config: shippingConfig,
            withTracking: true,
          })}
        </>
      )}
      <Divider />
      {renderGridSection({ title: 'Payment', config: paymentConfig })}
    </div>
  );
};

FinancialDetails.propTypes = {
  ...WithLoadingPropTypes,
  accountingConfig: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string,
      isModified: PropTypes.bool,
      authors: PropTypes.arrayOf(PropTypes.object),
    }),
  ),
  shippingConfig: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string,
      isModified: PropTypes.bool,
      authors: PropTypes.arrayOf(PropTypes.object),
      trackingInfo: PropTypes.shape({
        carrier: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    }),
  ),
  paymentConfig: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string,
      isModified: PropTypes.bool,
      authors: PropTypes.arrayOf(PropTypes.object),
    }),
  ),
};

export default FinancialDetails;
